// components/tour/TourOverlay.jsx
import React, { useState, useEffect } from 'react';
import { useTour } from './TourContext';
import TourTooltip from './TourTooltip';
import TourWelcome from './TourWelcome';
import TourComplete from './TourComplete';

const TourOverlay = () => {
  const {
    isActive,
    currentStep,
    tourConfig,
    isWelcomeVisible,
    isCompleteVisible,
    nextStep,
    previousStep,
    skipTour,
    endTour,
    getCurrentStepConfig,
    getTotalSteps,
  } = useTour();

  const [elementRect, setElementRect] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Simple scroll to element - no complex logic
  const scrollToElement = (element, stepConfig) => {
    if (!element) return Promise.resolve();
    
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const windowHeight = window.innerHeight;
    
    // Simple scroll positioning - just get element into view
    const targetScrollPosition = Math.max(0, absoluteElementTop - windowHeight / 3);
    
    window.scrollTo({
      top: targetScrollPosition,
      behavior: 'smooth'
    });
    
    return new Promise(resolve => setTimeout(resolve, 300));
  };

  useEffect(() => {
    const setupStep = async () => {
      setIsTransitioning(true);
      
      const stepConfig = getCurrentStepConfig();
      if (!stepConfig?.target) {
        setIsTransitioning(false);
        return;
      }

      // Find the target element
      const element = document.querySelector(stepConfig.target);
      if (!element) {
        console.warn(`Tour target not found: ${stepConfig.target}`);
        setIsTransitioning(false);
        return;
      }

      // Scroll to element
      await scrollToElement(element, stepConfig);
      
      // Update element rect - FIX: Use getBoundingClientRect() directly
      const updateElementRect = () => {
        const rect = element.getBoundingClientRect();
        setElementRect({
          top: rect.top + window.scrollY,  // FIXED: Correct calculation
          left: rect.left + window.scrollX, // FIXED: Correct calculation
          width: rect.width,
          height: rect.height,
        });
      };

      updateElementRect();

      // Small delay then show tooltip
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);

      // Update position on scroll/resize
      const handleUpdate = () => updateElementRect();
      window.addEventListener('scroll', handleUpdate);
      window.addEventListener('resize', handleUpdate);

      return () => {
        window.removeEventListener('scroll', handleUpdate);
        window.removeEventListener('resize', handleUpdate);
      };
    };

    if (isActive && !isWelcomeVisible && !isCompleteVisible) {
      setupStep();
    } else {
      setIsTransitioning(false);
    }
  }, [isActive, currentStep, isWelcomeVisible, isCompleteVisible, getCurrentStepConfig]);

  // Arrow key support
  useEffect(() => {
    if (!isActive || isWelcomeVisible || isCompleteVisible) return;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        
        if (event.key === 'ArrowLeft' && currentStep > 0) {
          previousStep();
        } else if (event.key === 'ArrowRight') {
          nextStep();
        }
      }
      
      if (event.key === 'Escape') {
        endTour();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, currentStep, isWelcomeVisible, isCompleteVisible, nextStep, previousStep, endTour]);

  if (!isActive) return null;

  // Show welcome screen
  if (isWelcomeVisible) {
    return <TourWelcome />;
  }

  // Show completion screen
  if (isCompleteVisible) {
    return <TourComplete />;
  }

  const stepConfig = getCurrentStepConfig();
  if (!stepConfig || !elementRect || isTransitioning) return null;

  // FIXED: Correct spotlight positioning
  const spotlightStyle = {
    position: 'absolute',
    top: `${elementRect.top}px`,
    left: `${elementRect.left}px`,
    width: `${elementRect.width}px`,
    height: `${elementRect.height}px`,
    borderRadius: '12px',
    boxShadow: `
      0 0 0 4px rgba(59, 130, 246, 0.8),
      0 0 0 8px rgba(59, 130, 246, 0.4),
      0 0 0 ${Math.max(window.innerWidth, window.innerHeight) * 2}px rgba(0, 0, 0, 0.6)
    `,
    backgroundColor: 'transparent',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    zIndex: 9999,
  };

  return (
    <>
      {/* Dark overlay with spotlight cutout */}
      <div style={spotlightStyle} />

      {/* Tour tooltip */}
      <TourTooltip
        step={stepConfig}
        currentStep={currentStep}
        totalSteps={getTotalSteps()}
        onNext={nextStep}
        onPrevious={previousStep}
        onSkip={skipTour}
        onClose={endTour}
        elementRect={elementRect}
      />
    </>
  );
};

export default TourOverlay;